import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { collection, getDocs, orderBy, query } from 'firebase/firestore';
import { db } from '../firebase';
import { useAuth } from '../context/AuthContext';
import { Spinner } from '../components/ui';
import {
  ActionChip,
  CalendarIcon,
  ChevronRightIcon,
  DocIcon,
  MessageIcon,
  PageHeader,
  PulseIcon,
  SectionCard,
  UploadIcon,
} from '../components/layout/memberPrimitives';
import {
  ScheduledTestRecord,
  formatScheduledTestCategory,
  formatScheduledTestStatus,
  listUpcomingScheduledTests,
} from '../services/scheduledTestService';
import { listMemberVisibleLabResults } from '../services/observationService';
import { listResultDraws, type ResultDraw } from '../utils/dashboard/newResults';

interface UploadedDocSummary {
  id: string;
  fileName: string;
  uploadedAt?: any;
}

interface TestingTile {
  icon: React.ReactNode;
  title: string;
  body: string;
  ctaLabel: string;
  href: string;
  accent: 'olive' | 'sea' | 'terracotta' | 'sun';
}

const ACCENT_BG: Record<TestingTile['accent'], string> = {
  olive: 'rgba(79, 93, 24, 0.14)',
  sea: 'rgba(168, 196, 211, 0.28)',
  terracotta: 'rgba(228, 183, 156, 0.35)',
  sun: 'rgba(94, 66, 24, 0.22)',
};

const ACCENT_FG: Record<TestingTile['accent'], string> = {
  olive: 'var(--melio-olive-deep)',
  sea: 'var(--melio-sea-deep)',
  terracotta: 'var(--melio-terracotta-deep)',
  sun: '#8A6A24',
};

function TestingTileCard({ icon, title, body, ctaLabel, href, accent }: TestingTile) {
  return (
    <Link
      to={href}
      className="melio-lift rounded-2xl border p-5 md:p-6 bg-white flex gap-4"
      style={{ borderColor: 'var(--melio-border)' }}
    >
      <div
        aria-hidden
        className="shrink-0 rounded-2xl flex items-center justify-center"
        style={{ width: 48, height: 48, backgroundColor: ACCENT_BG[accent], color: ACCENT_FG[accent] }}
      >
        {icon}
      </div>
      <div className="min-w-0 flex-1">
        <p
          className="text-lg font-normal leading-tight mb-1.5"
          style={{ fontFamily: "var(--melio-display)", color: 'var(--melio-ink)' }}
        >
          {title}
        </p>
        <p className="text-sm mb-3" style={{ color: 'var(--melio-text-muted)' }}>{body}</p>
        <span
          className="inline-flex items-center gap-1 text-xs font-medium"
          style={{ color: 'var(--melio-olive-deep)' }}
        >
          {ctaLabel} <ChevronRightIcon size={12} />
        </span>
      </div>
    </Link>
  );
}

function formatDate(value?: any): string {
  if (!value) return '';
  const date = value?.toDate ? value.toDate() : new Date(value);
  if (!date || Number.isNaN(date.getTime())) return '';
  return date.toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' });
}

export default function MemberTestingPage() {
  const { currentUser } = useAuth();
  const [loading, setLoading] = useState(true);
  const [uploads, setUploads] = useState<UploadedDocSummary[]>([]);
  const [upcomingTests, setUpcomingTests] = useState<ScheduledTestRecord[]>([]);
  // Tests that have already come BACK. This page is where a member goes after a
  // blood test — it is the rail item with a flask on it — and it used to talk
  // only about tests in the future, so someone whose panel had just landed was
  // told "Nothing booked yet".
  const [draws, setDraws] = useState<ResultDraw[]>([]);

  useEffect(() => {
    const load = async () => {
      if (!currentUser) return;
      try {
        const [docsSnap, scheduled, labResults] = await Promise.all([
          getDocs(
            query(
              collection(db, 'users', currentUser.uid, 'medical_documents'),
              orderBy('uploadedAt', 'desc'),
            ),
          ),
          listUpcomingScheduledTests(currentUser.uid),
          // Sex only decides which reference range a marker is judged against.
          // This card states no verdicts, so it is not read here.
          listMemberVisibleLabResults(currentUser.uid).catch(() => []),
        ]);
        setDraws(listResultDraws(labResults, null));
        setUploads(
          docsSnap.docs.map((document) => {
            const data = document.data() as Record<string, any>;
            return {
              id: document.id,
              fileName: data.fileName || data.name || 'Untitled file',
              uploadedAt: data.uploadedAt,
            };
          }),
        );
        setUpcomingTests(scheduled);
      } finally {
        setLoading(false);
      }
    };
    load();
  }, [currentUser]);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <Spinner size="lg" />
      </div>
    );
  }

  const tiles: TestingTile[] = [
    {
      icon: <UploadIcon size={22} />,
      title: 'Upload an outside result',
      body: 'Blood work, imaging or letters from clinicians outside Melio — anything that gives context before your next review. Results from tests we arranged arrive on their own.',
      ctaLabel: 'Upload document',
      href: '/results',
      accent: 'olive',
    },
    {
      icon: <CalendarIcon size={22} />,
      title: 'Book a consult',
      body: 'Add a clinician consult — new concern, follow-up question, or an add-on appointment outside your quarterly cadence.',
      ctaLabel: 'Open booking',
      href: '/enrollment',
      accent: 'sun',
    },
    {
      icon: <PulseIcon size={22} />,
      title: 'Connect a wearable',
      body: 'Link Whoop, Oura, Apple Health or Garmin so trends show up automatically in your Intelligence view.',
      ctaLabel: 'Set up wearables',
      href: '/intelligence',
      accent: 'sea',
    },
    {
      icon: <MessageIcon size={22} />,
      title: 'Ask about a test',
      body: 'Not sure whether a specific panel, scan, or genetic test is useful for you? Message your care team and we\u2019ll advise.',
      ctaLabel: 'Open messages',
      href: '/messages',
      accent: 'terracotta',
    },
  ];

  return (
    <div className="max-w-5xl mx-auto px-4 md:px-8 py-8 md:py-10 melio-stagger">
      <PageHeader
        breadcrumb={[{ label: 'Home', href: '/home' }, { label: 'Testing' }]}
        eyebrow="Tests & add-ons"
        title="Testing"
        subtitle="Results that have come back, tests that are coming up, and anything you want to add."
      />

      {/* Results first. A member arriving here has usually just HAD a test, not
          just booked one, and the page answered only the second case. */}
      {draws.length > 0 && (
        <SectionCard
          eyebrow="Results back"
          title={
            draws[0].panelLabel
              ? `${draws[0].panelLabel} — ${formatDate(draws[0].collectedAt || draws[0].observedAt)}`
              : `Your results from ${formatDate(draws[0].collectedAt || draws[0].observedAt)}`
          }
          rightSlot={
            <Link to="/results" className="text-xs underline" style={{ color: 'var(--melio-olive-deep)' }}>
              Open your results →
            </Link>
          }
          className="mb-6"
        >
          <p className="text-sm leading-relaxed mb-4" style={{ color: 'var(--melio-text-muted)' }}>
            {draws[0].count === 1 ? '1 marker' : `${draws[0].count} markers`} came back on this draw
            {draws[0].labLabel ? `, via ${draws[0].labLabel}` : ''}. Open Results to see each one against
            your reference range.
          </p>
          {/* Counts only, and no verdict. Whether a marker matters is read on
              the results page next to its range, and in the report — not from a
              status pill on a navigation card. */}
          <div className="flex flex-wrap gap-2">
            <ActionChip label="See your results" href="/results" />
            {draws.length > 1 && (
              <span className="text-xs self-center" style={{ color: 'var(--melio-text-muted)' }}>
                {draws.length} draws on file
              </span>
            )}
          </div>
        </SectionCard>
      )}

      <SectionCard
        eyebrow="Upcoming tests"
        title={upcomingTests.length > 0 ? `${upcomingTests.length} scheduled` : 'Nothing booked yet'}
        rightSlot={
          upcomingTests.length > 0 ? (
            <Link to="/messages" className="text-xs underline" style={{ color: 'var(--melio-olive-deep)' }}>
              Questions about a test? →
            </Link>
          ) : null
        }
        className="mb-6"
      >
        {upcomingTests.length === 0 ? (
          <div>
            <p className="text-sm leading-relaxed mb-3" style={{ color: 'var(--melio-text-muted)' }}>
              When your clinician plans a blood panel, genetic kit, or follow-up off the back of a
              quarterly review, it&rsquo;ll appear here with the date, where to go, and any prep you need to do.
            </p>
            <p className="text-sm leading-relaxed" style={{ color: 'var(--melio-text-muted)' }}>
              Want something added sooner? Use the tiles below or{' '}
              <Link to="/messages" className="underline" style={{ color: 'var(--melio-olive-deep)' }}>
                message your care team
              </Link>
              .
            </p>
          </div>
        ) : (
          <ul className="space-y-3">
            {upcomingTests.map((test) => {
              const dateLabel = formatDate(test.scheduledFor);
              return (
                <li
                  key={test.id}
                  className="rounded-xl border px-4 py-4 flex items-start gap-4"
                  style={{ borderColor: 'var(--melio-border)', backgroundColor: 'var(--melio-cream-warm)' }}
                >
                  <div
                    className="shrink-0 rounded-lg px-3 py-2 text-[11px] uppercase tracking-wide font-medium text-center min-w-[70px]"
                    style={{
                      backgroundColor: 'rgba(79, 93, 24, 0.14)',
                      color: 'var(--melio-olive-deep)',
                    }}
                  >
                    {dateLabel || 'TBD'}
                  </div>
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-2 flex-wrap">
                      <p
                        className="text-base font-normal leading-tight"
                        style={{ fontFamily: "var(--melio-display)", color: 'var(--melio-ink)' }}
                      >
                        {test.name}
                      </p>
                      <span
                        className="text-[10px] uppercase tracking-wide rounded-full px-2 py-0.5"
                        style={{
                          backgroundColor: 'rgba(168, 196, 211, 0.28)',
                          color: 'var(--melio-sea-deep)',
                        }}
                      >
                        {formatScheduledTestCategory(test.category)}
                      </span>
                      <span
                        className="text-[10px] uppercase tracking-wide rounded-full px-2 py-0.5"
                        style={{
                          backgroundColor: 'rgba(94, 66, 24, 0.22)',
                          color: '#8A6A24',
                        }}
                      >
                        {formatScheduledTestStatus(test.status)}
                      </span>
                    </div>
                    {(test.provider || test.location) && (
                      <p className="text-xs mt-1" style={{ color: 'var(--melio-text-muted)' }}>
                        {[test.provider, test.location].filter(Boolean).join(' · ')}
                      </p>
                    )}
                    {test.instructions && (
                      <p className="text-sm mt-2 leading-relaxed" style={{ color: 'var(--melio-charcoal)' }}>
                        {test.instructions}
                      </p>
                    )}
                  </div>
                </li>
              );
            })}
          </ul>
        )}
      </SectionCard>

      <SectionCard eyebrow="Your testing cadence" title="Quarterly reviews">
        <p className="text-sm leading-relaxed" style={{ color: 'var(--melio-text-muted)' }}>
          Your membership includes a clinician-reviewed quarterly report drawn from your wearable data,
          intake, and any results you upload. Scheduled blood work and add-on tests will appear here
          once we&rsquo;ve planned your first review.
        </p>
        <div className="flex flex-wrap gap-2 mt-4">
          <ActionChip label="View reports" href="/reports" />
          <ActionChip label="See sample report" href="/reports/sample" />
        </div>
      </SectionCard>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-6">
        {tiles.map((tile) => (
          <TestingTileCard key={tile.title} {...tile} />
        ))}
      </div>

      <SectionCard
        eyebrow="Documents"
        title="What you’ve shared"
        rightSlot={
          <Link to="/results" className="text-xs underline" style={{ color: 'var(--melio-olive-deep)' }}>
            Manage uploads →
          </Link>
        }
        className="mt-6"
      >
        {uploads.length === 0 ? (
          <p className="text-sm" style={{ color: 'var(--melio-text-muted)' }}>
            No documents on file yet. Upload a past lab result or clinician letter and it&rsquo;ll be here next time you visit.
          </p>
        ) : (
          <ul className="space-y-2">
            {uploads.slice(0, 6).map((upload) => (
              <li
                key={upload.id}
                className="rounded-xl border px-4 py-3 flex items-center gap-3"
                style={{ borderColor: 'var(--melio-border)', backgroundColor: 'var(--melio-cream-warm)' }}
              >
                <DocIcon size={16} />
                <div className="min-w-0 flex-1">
                  <p className="text-sm font-medium truncate" style={{ color: 'var(--melio-ink)' }}>
                    {upload.fileName}
                  </p>
                  <p className="text-xs" style={{ color: 'var(--melio-text-muted)' }}>
                    {formatDate(upload.uploadedAt) || 'Uploaded'}
                  </p>
                </div>
              </li>
            ))}
            {uploads.length > 6 && (
              <li>
                <Link
                  to="/results"
                  className="text-xs underline"
                  style={{ color: 'var(--melio-text-muted)' }}
                >
                  Show all {uploads.length} documents →
                </Link>
              </li>
            )}
          </ul>
        )}
      </SectionCard>
    </div>
  );
}
